/**
 * Phase Action Modal Component
 *
 * Generic modal for viewing and editing phase details and subitems
 *
 * Features:
 * - View mode: displays phase information and subitems
 * - Edit mode: displays same with an action link to full editor
 * - Handles navigation to table view for editing subitems
 */

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogClose,
} from "@/components/ui/dialog";
import { BudgetPhase, BudgetLineItem } from "@/types/orcamentos.interface";
import { X } from "lucide-react";

/**
 * Format currency to Brazilian Real
 */
function formatCurrency(value: number): string {
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(value);
}

interface PhaseActionModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    phase: BudgetPhase | null;
    onEditSubitems?: () => void;
}

export function PhaseActionModal({
    open,
    onOpenChange,
    phase,
    onEditSubitems,
}: PhaseActionModalProps) {
    if (!phase) return null;

    // Flatten line items from all topics
    const lineItems: BudgetLineItem[] = phase.topics
        ? phase.topics.flatMap((topic) => topic.lineItems || [])
        : [];

    const title = `Visualizar Fase - ${phase.phaseName}`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[800px] bg-[#15203C] border-[#7846FF] rounded-[20px] px-[30px] py-[30px] overflow-y-auto max-h-[85vh]">
                <DialogHeader className="flex flex-row items-center justify-between mb-4">
                    <DialogTitle className="font-['Plus_Jakarta_Sans'] font-bold text-[24px] text-white">
                        {title}
                    </DialogTitle>
                    <DialogClose className="text-[#F6F6F6] hover:text-white transition-colors flex items-center gap-2">
                        <span className="font-semibold text-[16px]">Fechar</span>
                        <X size={24} />
                    </DialogClose>
                </DialogHeader>

                {/* Divider */}
                <div className="h-px border-t border-gray-700 mb-6" />

                <div className="flex flex-col gap-6">
                    {/* Phase Summary */}
                    <div className="flex gap-10">
                        <div className="flex flex-col">
                            <span className="text-[#888888] text-sm mb-1">Orçamento Executado</span>
                            <span className="text-[#13D480] text-xl font-bold">
                                {formatCurrency(phase.orcamentoExecutado)}
                            </span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[#888888] text-sm mb-1">Orçamento Previsto</span>
                            <span className="text-white text-xl font-bold">
                                {formatCurrency(phase.orcamentoAprovado)}
                            </span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[#888888] text-sm mb-1">% Executado</span>
                            <span className="text-white text-xl font-bold">
                                {phase.percentual}%
                            </span>
                        </div>
                    </div>

                    {/* Subitems List */}
                    <div className="mt-4">
                        <h4 className="text-white font-bold text-[18px] mb-4">Subitens</h4>
                        {lineItems.length === 0 ? (
                            <p className="text-[#888888] text-sm">Nenhum subitem encontrado nesta fase.</p>
                        ) : (
                            <div className="flex flex-col gap-3">
                                {lineItems.map((item, idx) => (
                                    <div
                                        key={item.id || idx}
                                        className="flex items-center justify-between bg-[rgba(12,20,39,0.5)] border border-[rgba(93,93,93,0.3)] rounded-[10px] p-4"
                                    >
                                        <div className="flex flex-col gap-1">
                                            <span className="text-[#F6F6F6] font-semibold">
                                                {item.name}
                                            </span>
                                            <span className="text-[#888888] text-[13px]">
                                                Atualizado: {item.lastUpdate || "-"}
                                            </span>
                                        </div>
                                        <div className="flex gap-8 text-right">
                                            <div className="flex flex-col">
                                                <span className="text-[#888888] text-[12px]">Previsto</span>
                                                <span className="text-[#F6F6F6] font-medium">
                                                    {formatCurrency(item.orcamentoPrevisto)}
                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[#888888] text-[12px]">Executado</span>
                                                <span className="text-[#13D480] font-medium">
                                                    {formatCurrency(item.value)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-2 pt-4 border-t border-gray-700 flex justify-end">
                        <button
                            type="button"
                            onClick={() => {
                                if (onEditSubitems) onEditSubitems();
                            }}
                            className="text-[#7846FF] hover:text-[#9b6fff] font-bold text-[16px] transition-colors underline"
                        >
                            Editar subitens na tabela orçamentária
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
